/**
 * Cryptographically Secure ID Generation Utilities
 *
 * Replaces Math.random() with crypto.getRandomValues() for generating
 * unique identifiers. Math.random() is not cryptographically secure
 * and should not be used for generating IDs that need to be unpredictable.
 *
 * SECURITY: Uses Web Crypto API (crypto.getRandomValues) which provides
 * cryptographically strong random values, suitable for:
 * - Session identifiers
 * - Tab identifiers
 * - Message IDs
 * - Any ID that could be used in security-sensitive contexts
 */

/**
 * Generate a cryptographically secure random string.
 *
 * @param length - The length of the output string (default: 9)
 * @returns A random alphanumeric string
 *
 * @example
 * ```typescript
 * const id = generateSecureRandomString(12);
 * // Returns something like: "k8x9m2n4p7q1"
 * ```
 */
export function generateSecureRandomString(length: number = 9): string {
  // Use crypto.getRandomValues for cryptographically secure randomness
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);

  // Convert to base36 string (0-9, a-z)
  return Array.from(array)
    .map((b) => (b % 36).toString(36))
    .join('')
    .substring(0, length);
}

/**
 * Generate a unique ID with timestamp prefix.
 * Format: {prefix}_{timestamp}_{random}
 *
 * @param prefix - Optional prefix for the ID (default: empty)
 * @returns A unique identifier string
 *
 * @example
 * ```typescript
 * const stageId = generateUniqueId('stage');
 * // Returns: "stage_lxyz123_k8x9m2n4p"
 *
 * const tabId = generateUniqueId('tab');
 * // Returns: "tab_lxyz123_k8x9m2n4p"
 * ```
 */
export function generateUniqueId(prefix?: string): string {
  const timestamp = Date.now().toString(36);
  const random = generateSecureRandomString(9);

  if (prefix) {
    return `${prefix}_${timestamp}_${random}`;
  }

  return `${timestamp}_${random}`;
}

/**
 * Generate a message/chat ID.
 * Produces a shorter ID suitable for chat messages.
 *
 * @returns A unique message identifier
 *
 * @example
 * ```typescript
 * const msgId = generateMessageId();
 * // Returns: "k8x9m2n4p7q1lxyz123"
 * ```
 */
export function generateMessageId(): string {
  const random = generateSecureRandomString(11);
  const timestamp = Date.now().toString(36);
  return `${random}${timestamp}`;
}

/**
 * Generate a session ID.
 * Format: {timestamp}-{random}
 *
 * @returns A unique session identifier
 *
 * @example
 * ```typescript
 * const sessionId = generateSessionId();
 * // Returns: "lxyz123abc-k8x9m2n4p7q1w"
 * ```
 */
export function generateSessionId(): string {
  const timestamp = Date.now().toString(36);
  const random = generateSecureRandomString(13);
  return `${timestamp}-${random}`;
}

/**
 * Generate a UUID v4 using crypto API.
 * Falls back to crypto.randomUUID() if available.
 *
 * @returns A UUID v4 string
 *
 * @example
 * ```typescript
 * const uuid = generateUUID();
 * // Returns: "550e8400-e29b-41d4-a716-446655440000"
 * ```
 */
export function generateUUID(): string {
  // Prefer native randomUUID if available
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback implementation using getRandomValues
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Set version (4) and variant (10xx) bits
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  // Convert to hex string with dashes
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
